import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, Plus, Trash2, Save, Languages, CheckCircle2, HelpCircle } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { useFAQ } from '@/services/use-faq'

const localizedStringSchema = z.object({
  tr: z.string().min(1, 'Türkçe alan zorunludur'),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const faqSchema = z.object({
  hero: z.object({
    title: localizedStringSchema,
    description: localizedStringSchema,
  }),
  faqs: z.array(z.object({
    question: localizedStringSchema,
    answer: localizedStringSchema,
    category: localizedStringSchema.optional(),
  })),
})

type FAQFormValues = z.infer<typeof faqSchema>

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'SSS CMS', href: '/sss', isActive: true },
]

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
]

export default function SSS() {
  const { faq, isLoading, updateFAQ, isUpdating } = useFAQ()
  const [isNormalSaving, setIsNormalSaving] = useState(false)
  const [isAISaving, setIsAISaving] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')

  const form = useForm<FAQFormValues>({
    resolver: zodResolver(faqSchema),
    defaultValues: {
      hero: { title: { tr: '' }, description: { tr: '' } },
      faqs: [],
    },
  })

  const { fields: faqFields, append: appendFAQ, remove: removeFAQ } = useFieldArray({
    control: form.control,
    name: 'faqs',
  })

  useEffect(() => {
    if (faq) {
      form.reset({
        hero: faq.hero,
        faqs: faq.faqs,
      })
    }
  }, [faq, form])

  const onSubmit = async (values: FAQFormValues) => {
    setIsNormalSaving(true)
    try {
      await updateFAQ({ data: values })
    } finally {
      setIsNormalSaving(false)
    }
  }

  const onTranslateAndSave = async () => {
    const values = form.getValues()
    setIsAISaving(true)
    setShowProgress(true)
    setProgress(5)
    setCurrentStep('AI Çeviri servisine bağlanılıyor...')

    const steps = [
      { p: 20, m: 'Giriş (Hero) bölümü çeviriliyor...' },
      { p: 60, m: 'Tüm sorular ve cevaplar çeviriliyor...' },
      { p: 90, m: 'Kategoriler düzenleniyor...' },
      { p: 98, m: 'Veriler işleniyor, bu biraz zaman alabilir...' },
    ]

    let currentStepIdx = 0
    const interval = setInterval(() => {
      if (currentStepIdx < steps.length) {
        setProgress(steps[currentStepIdx].p)
        setCurrentStep(steps[currentStepIdx].m)
        currentStepIdx++
      }
    }, 1200)

    try {
      await updateFAQ({ data: values, translate: true })
      clearInterval(interval)
      setProgress(100)
      setCurrentStep('Tüm diller başarıyla kaydedildi!')
      setTimeout(() => setShowProgress(false), 2000)
    } catch (error) {
      clearInterval(interval)
      setShowProgress(false)
    } finally {
      setIsAISaving(false)
    }
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">SSS Yönetimi</h1>
                <p className="text-muted-foreground text-sm">Sıkça Sorulan Sorular ve cevaplarını düzenleyin.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onTranslateAndSave}
                  disabled={isUpdating}
                  className="w-full sm:w-auto border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  {isAISaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  AI ile Çevir ve Kaydet
                </Button>
                <Button type="submit" disabled={isUpdating} className="w-full sm:w-auto">
                  {isNormalSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Normal Kaydet
                </Button>
              </div>
            </div>

            <Tabs defaultValue="hero" className="w-full">
              <TabsList className="flex flex-wrap h-auto w-full">
                <TabsTrigger value="hero" className="flex-1">Giriş (Hero)</TabsTrigger>
                <TabsTrigger value="faqs" className="flex-1">Sorular & Cevaplar</TabsTrigger>
              </TabsList>

              {/* HERO SECTION */}
              <TabsContent value="hero" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Sayfa Başlığı ve Açıklama</CardTitle>
                    <CardDescription>SSS sayfasının en üstünde görünen karşılama metni.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr" className="w-full">
                      <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`hero.title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: Sıkça Sorulan Sorular" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`hero.description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Açıklama ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} placeholder="SSS sayfası açıklaması..." rows={3} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* FAQS SECTION */}
              <TabsContent value="faqs" className="space-y-4 pt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => appendFAQ({ question: { tr: '' }, answer: { tr: '' }, category: { tr: 'Genel' } })}>
                    <Plus className="mr-2 h-4 w-4" /> Soru Ekle
                  </Button>
                </div>
                <div className="grid gap-6">
                  {faqFields.map((field, index) => (
                    <Card key={field.id} className="relative">
                      <CardHeader className="flex flex-row items-center justify-between pb-2 border-b mb-4">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <HelpCircle className="h-4 w-4 text-primary" />
                          Soru #{index + 1}
                        </CardTitle>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:bg-destructive/10" onClick={() => removeFAQ(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <Tabs defaultValue="tr" className="w-full">
                          <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                              <FormField
                                control={form.control}
                                name={`faqs.${index}.category.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-xs">Kategori ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Örn: Genel, Teslimat vb." className="h-8 text-sm" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`faqs.${index}.question.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Soru ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Soruyu yazın..." />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`faqs.${index}.answer.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Cevap ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Textarea {...field} placeholder="Cevabı yazın..." rows={3} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {faqFields.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg bg-muted/20">
                    <HelpCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Henüz soru eklenmemiş.</p>
                    <Button type="button" variant="link" onClick={() => appendFAQ({ question: { tr: '' }, answer: { tr: '' }, category: { tr: 'Genel' } })}>
                      İlk soruyu ekle
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </Main>

      <Dialog open={showProgress} onOpenChange={setShowProgress}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-blue-600" />
              Yapay Zeka Çevirisi
            </DialogTitle>
            <DialogDescription>
              İçeriğiniz 7 farklı dile profesyonelce çevriliyor. Lütfen bekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-medium">
                <span>{currentStep}</span>
                <span>%{progress}</span>
              </div>
              <Progress value={progress} className="h-2 w-full" />
            </div>
            {progress === 100 && (
              <div className="flex items-center justify-center gap-2 text-green-600 animate-in fade-in zoom-in duration-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium text-sm">Başarıyla tamamlandı ve kaydedildi!</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
